import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { AUTH_API_ROUTES } from '../constants/auth.constant';
import { AuthService } from './auth.service';
import { HeaderService } from './header.service';
import { Router } from '@angular/router';

/**
 * Functional HTTP interceptor responsible for handling authentication
 * and custom HTTP headers for outgoing requests.
 * The interceptor:
 * - Adds URL-specific headers configured through `HeaderService`.
 * - Adds an `Authorization: Bearer <token>` header when a JWT token exists.
 * - Prevents authentication headers from being added to authentication
 *   endpoints.
 * - Handles `401` and `403` HTTP responses.
 * - Attempts to refresh the authentication token for eligible `401` responses.
 * - Redirects the user to the sign-in page when authentication fails.
 * @param req HTTP request being intercepted.
 * @param next Function used to forward the request to the next interceptor
 * or HTTP backend.
 * @returns Observable containing the HTTP event stream.
 */
export const tokenInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const headerService = inject(HeaderService);

  /**
   * Service used to manage authentication and token refresh operations.
   */
  const authService = inject(AuthService);

  /**
   * Router used to redirect unauthenticated users to the sign-in page.
   */
  const router = inject(Router);

  return next(addHeaders(req, headerService)).pipe(
    catchError((error) => handleAuthError(error, req, next, headerService, authService, router))
  );
};

/**
 * Adds configured custom headers and the authentication token
 * to an outgoing HTTP request.
 * URL-specific headers are retrieved from `HeaderService` and merged
 * into the request. If a JWT token exists in local storage and the
 * request is not an authentication request, an Authorization header
 * is added when one is not already present.
 * @param req HTTP request to modify.
 * @param headerService Service used to retrieve configured headers.
 * @returns A cloned HTTP request containing the required headers.
 */
function addHeaders(req: HttpRequest<unknown>, headerService: HeaderService): HttpRequest<unknown> {
  let headers = req.headers;

  /**
   * Retrieves custom headers configured for the requested URL.
   */
  const customHeaders = headerService.getHeaders(req.url);
  customHeaders.keys().forEach((name) => {
    const values = customHeaders.getAll(name);
    if (values) {
      headers = headers.set(name, values);
    }
  });

  /**
   * Retrieves the JWT token stored in local storage.
   */
  const token = localStorage.getItem('jwt_token');

  /**
   * Adds the Authorization header when:
   * - A token exists.
   * - The request does not already contain an Authorization header.
   * - The request is not an authentication endpoint.
   */
  if (
    token &&
    !headers.has('Authorization') &&
    !isAuthenticationRequest(req.url)
  ) {
    headers = headers.set(
      'Authorization',
      token.startsWith('Bearer ')
        ? token
        : `Bearer ${token}`,
    );
  }
  return req.clone({ headers });
}
/**
 * Handles authentication-related HTTP errors.
 * For `401` responses, attempts to refresh the authentication token
 * when the request is not an authentication request. If the refresh
 * succeeds, the original request is retried with the refreshed token.
 * For unsuccessful token refresh operations or `403` responses,
 * the user is redirected to the sign-in page.
 * Non-authentication-related errors are re-thrown unchanged.
 * @param error Error returned from the HTTP request.
 * @param req Original HTTP request that produced the error.
 * @param next Function used to forward the HTTP request.
 * @param headerService Service used to retrieve configured HTTP headers.
 * @param authService Service used to refresh the authentication token.
 * @param router Router used to navigate the user to the sign-in page.
 * @returns Observable containing the retried HTTP request or the
 * original error.
 */
function handleAuthError(
  error: unknown,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  headerService: HeaderService,
  authService: AuthService,
  router: Router,
): Observable<HttpEvent<unknown>> {
  /**
   * Handles unauthorized and forbidden responses.
   */
  if (
    error instanceof HttpErrorResponse &&
    (error.status === 401 || error.status === 403)
  ) {
    /**
     * Attempts token refresh for unauthorized requests that are
     * not authentication-related requests.
     */
    if (
      error.status === 401 &&
      !isAuthenticationRequest(req.url)
    ) {
      return authService.refreshToken().pipe(
        switchMap((success: boolean) => {
          /**
           * Retry the original request when token refresh succeeds.
           */
          if (success) {
            return next(addHeaders(req, headerService));
          }

          /**
           * Redirect to sign-in when token refresh fails.
           */
          router.navigate(['/signin']);

          return throwError(() => error);
        }),
      );
    } else {
      /**
       * Redirect authentication-related unauthorized or forbidden
       * requests to the sign-in page.
       */
      router.navigate(['/signin']);
    }
  }

  /**
   * Re-throws the error so it can be handled by the caller
   * or the application's global error handling mechanism.
   */
  return throwError(() => error);
}

/**
 * Determines whether a URL belongs to an authentication endpoint.
 * Authentication endpoints do not receive the Authorization header
 * because they are used for credential exchange, password recovery,
 * token verification, or token refresh operations.
 * @param url URL of the HTTP request.
 * @returns `true` when the URL is an authentication endpoint;
 * otherwise, `false`.
 */
function isAuthenticationRequest(url: string): boolean {
  /**
   * Authentication routes that must not receive an Authorization header.
   */
  const noAuthHeaderRoutes = [
    AUTH_API_ROUTES.SIGN_IN,
    AUTH_API_ROUTES.REFRESH_TOKEN,
    AUTH_API_ROUTES.FORGOT_PASSWORD,
    AUTH_API_ROUTES.VERIFY_TOKEN,
    AUTH_API_ROUTES.RESET_PASSWORD,
  ];
  return noAuthHeaderRoutes.some((route) => url.includes(route));
}